import { createContext, useContext, useState, useCallback, lazy, Suspense, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import lazyWithReload from '../../utils/lazyWithReload';
import DrawerLoadingFallback from '../common/DrawerLoadingFallback';
const CrmInvoiceDrawer = lazyWithReload(() => import('./CrmInvoiceDrawer'), 'CrmInvoiceDrawer');
import { sid } from '../../utils/shortId';
import { registerEntityOpener } from '../../utils/openEntity';
import { noProviderOpener } from '../../utils/drawerNoProvider';
import { providerIfModuleOn } from '../../lib/hostModules';

/**
 * URL-driven `?invoiceId=<short>`.
 * API: useCrmInvoiceDrawer() → { openInvoice, closeInvoice, version, bumpVersion, openInvoiceId }
 */
const Ctx = createContext(null);

// Provider không nằm trên cây React của người gọi (hay gặp nhất: nội dung xem TRONG một
// drawer KHÁC) → lui về sổ đăng ký module-scope. Xem utils/drawerNoProvider.
const NO_PROVIDER = {
  openInvoice: noProviderOpener('invoice'),
  closeInvoice: () => {},
  version: 0,
  bumpVersion: () => {},
  openInvoiceId: null,
};

function CrmInvoiceDrawerProviderOn({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const invoiceId = searchParams.get('invoiceId');
  const [version, setVersion] = useState(0);

  const openInvoice = useCallback((id) => {
    if (!id) return;
    const short = sid(id);
    setSearchParams(prev => {
      if (prev.get('invoiceId') === short) return prev;
      const next = new URLSearchParams(prev);
      next.set('invoiceId', short);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeInvoice = useCallback(() => {
    setSearchParams(prev => {
      if (!prev.get('invoiceId')) return prev;
      const next = new URLSearchParams(prev);
      next.delete('invoiceId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const bumpVersion = useCallback(() => setVersion(v => v + 1), []);

  // Khai báo với kernel utils/openEntity: "loại này mở bằng POPUP" → link/card trong chat
  // trỏ tới bản ghi loại này bung popup tại chỗ, không nhảy trang chi tiết (trắng trang chờ chunk).
  useEffect(() => registerEntityOpener('invoice', openInvoice), [openInvoice]);

  return (
    <Ctx.Provider value={{ openInvoice, closeInvoice, version, bumpVersion, openInvoiceId: invoiceId }}>
      {children}
      {invoiceId && (
        <Suspense fallback={<DrawerLoadingFallback onClose={closeInvoice} />}>
          <CrmInvoiceDrawer open={!!invoiceId} invoiceId={invoiceId} onClose={closeInvoice} />
        </Suspense>
      )}
    </Ctx.Provider>
  );
}

export function useCrmInvoiceDrawer() {
  return useContext(Ctx) || NO_PROVIDER;
}

// Cụm CRM tắt ở host (`modules.crm = false` — Site Connect 12/10/2026) ⇒ Provider rỗng: không đăng ký popup, không
// đọc query param, không dựng drawer (nội dung drawer gọi hàng chục /crm/*). Xem lib/hostModules.
export const CrmInvoiceDrawerProvider = providerIfModuleOn('crm', CrmInvoiceDrawerProviderOn);
